import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingCart, FileText, BarChart3, Package, Clock, DollarSign, Calendar, Calculator, Home, Printer } from 'lucide-react';
import { useCustomerAuth } from '../../contexts/CustomerAuthContext';
import CustomerPortalLayout from '../../components/customer-portal/CustomerPortalLayout';
import { customerPortalService } from '../../services/customerPortalApi';

const formatMoney = (value, currency = 'USD') => {
  if (value == null || Number.isNaN(value)) return '-';
  const num = Number(value);
  return new Intl.NumberFormat('tr-TR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(num) + (currency === 'USD' ? ' $' : ' ₺');
};

const CustomerDashboard = () => {
  const navigate = useNavigate();
  const { customer, loading } = useCustomerAuth();
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);

  useEffect(() => {
    if (!customer?.id) return;
    let cancelled = false;
    const fetchStats = async () => {
      setStatsLoading(true);
      try {
        const res = await customerPortalService.getDashboardStats();
        if (!cancelled && res?.success && res?.data) setStats(res.data);
      } catch (_) {
        if (!cancelled) setStats(null);
      } finally {
        if (!cancelled) setStatsLoading(false);
      }
    };
    fetchStats();
    return () => { cancelled = true; };
  }, [customer?.id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <CustomerPortalLayout title="Müşteri Portalı" icon={Home} showBackButton={false}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="text-xs text-gray-600 dark:text-gray-400 mb-4">
          {customer?.fullName || customer?.companyName}
        </div>
      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Ana Özellikler - Mobilde tek sütun, Desktop'ta 5 kart */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 sm:gap-6 mb-6 sm:mb-8">
          {/* 1. Siparişler */}
          <div 
            onClick={() => navigate('/orders')}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-md hover:shadow-lg transition-shadow cursor-pointer border-2 border-transparent hover:border-blue-500"
          >
            <div className="p-4 sm:p-6">
              <div className="flex items-center justify-between mb-3 sm:mb-4">
                <div className="p-2 sm:p-3 bg-blue-100 dark:bg-blue-900 rounded-lg">
                  <ShoppingCart className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600 dark:text-blue-400" />
                </div>
              </div>
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-1.5 sm:mb-2">
                Siparişlerim
              </h3>
              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-3 sm:mb-4">
                Sipariş oluşturun, durumunu takip edin ve geçmiş siparişlerinizi görüntüleyin.
              </p>
              <div className="flex items-center text-blue-600 dark:text-blue-400 text-sm font-medium">
                <span>Detayları Gör</span>
                <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>
          </div>

          {/* 2. Dijital Baskı */}
          <div 
            onClick={() => navigate('/digital-printing')}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-md hover:shadow-lg transition-shadow cursor-pointer border-2 border-transparent hover:border-red-500"
          >
            <div className="p-4 sm:p-6">
              <div className="flex items-center justify-between mb-3 sm:mb-4">
                <div className="p-2 sm:p-3 bg-red-100 dark:bg-red-900 rounded-lg">
                  <Printer className="w-5 h-5 sm:w-6 sm:h-6 text-red-600 dark:text-red-400" />
                </div>
              </div>
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-1.5 sm:mb-2">
                Dijital Baskı
              </h3>
              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-3 sm:mb-4">
                Dijital baskı siparişlerinizi görüntüleyin, numune isteklerinizi takip edin.
              </p>
              <div className="flex items-center text-red-600 dark:text-red-400 text-sm font-medium">
                <span>Detayları Gör</span>
                <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>
          </div>

          {/* 3. Cari Hesap Hareketleri */}
          <div 
            onClick={() => navigate('/account-movements')}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-md hover:shadow-lg transition-shadow cursor-pointer border-2 border-transparent hover:border-green-500"
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 bg-green-100 dark:bg-green-900 rounded-lg">
                  <FileText className="w-6 h-6 text-green-600 dark:text-green-400" />
                </div>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Hesap Hareketleri
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Cari hesap bakiyenizi, ödemelerinizi ve alacaklarınızı görüntüleyin.
              </p>
              <div className="flex items-center text-green-600 dark:text-green-400 text-sm font-medium">
                <span>Detayları Gör</span>
                <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>
          </div>

          {/* 4. Analizler */}
          <div 
            onClick={() => navigate('/analytics')}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-md hover:shadow-lg transition-shadow cursor-pointer border-2 border-transparent hover:border-purple-500"
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 bg-purple-100 dark:bg-purple-900 rounded-lg">
                  <BarChart3 className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                </div>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Analizler
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Kumaş alımlarınız, ödemeleriniz ve teslimat süreleriniz hakkında detaylı analizler.
              </p>
              <div className="flex items-center text-purple-600 dark:text-purple-400 text-sm font-medium">
                <span>Detayları Gör</span>
                <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>
          </div>

          {/* 5. Fiyat Hesapla */}
          <div 
            onClick={() => navigate('/tools')}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-md hover:shadow-lg transition-shadow cursor-pointer border-2 border-transparent hover:border-orange-500"
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 bg-orange-100 dark:bg-orange-900 rounded-lg">
                  <Calculator className="w-6 h-6 text-orange-600 dark:text-orange-400" />
                </div>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Fiyat Hesapla
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Kumaş maliyetini hesaplayın ve farklı kumaşları karşılaştırın.
              </p>
              <div className="flex items-center text-orange-600 dark:text-orange-400 text-sm font-medium">
                <span>Detayları Gör</span>
                <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Özet İstatistikler */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded">
                <Package className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="ml-3">
                <p className="text-sm text-gray-600 dark:text-gray-400">Aktif Sipariş</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">
                  {statsLoading ? '...' : (stats != null ? stats.activeOrderCount : '-')}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 dark:bg-green-900 rounded">
                <DollarSign className="w-5 h-5 text-green-600 dark:text-green-400" />
              </div>
              <div className="ml-3">
                <p className="text-sm text-gray-600 dark:text-gray-400">Bakiye</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">
                  {statsLoading ? '...' : (stats != null ? (
                    (stats.balanceUsd !== 0 || stats.balanceTry !== 0)
                      ? (stats.balanceUsd !== 0 ? formatMoney(stats.balanceUsd, 'USD') : formatMoney(stats.balanceTry, 'TRY'))
                      : '0,00 $'
                  ) : '-')}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 dark:bg-purple-900 rounded">
                <Calendar className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div className="ml-3">
                <p className="text-sm text-gray-600 dark:text-gray-400">Son Sipariş</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">
                  {statsLoading ? '...' : (stats != null && stats.lastOrderDate ? new Date(stats.lastOrderDate).toLocaleDateString('tr-TR') : '-')}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div className="flex items-center">
              <div className="p-2 bg-orange-100 dark:bg-orange-900 rounded">
                <Clock className="w-5 h-5 text-orange-600 dark:text-orange-400" />
              </div>
              <div className="ml-3">
                <p className="text-sm text-gray-600 dark:text-gray-400">Ort. Teslimat</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">
                  {statsLoading ? '...' : (stats != null && stats.avgDeliveryDays != null ? `${stats.avgDeliveryDays} gün` : '-')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
      </div>
    </CustomerPortalLayout>
  );
};

export default CustomerDashboard;
